export function mean(values: readonly number[]): number {
  if (values.length === 0) return Number.NaN;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}
export function median(values: readonly number[]): number {
  if (values.length === 0) return Number.NaN;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  const upper = sorted[middle];
  if (upper === undefined) return Number.NaN;
  if (sorted.length % 2 === 1) return upper;
  const lower = sorted[middle - 1];
  return lower === undefined ? Number.NaN : (lower + upper) / 2;
}

export function quantile(values: readonly number[], probability: number): number {
  if (values.length === 0 || probability < 0 || probability > 1) return Number.NaN;
  const sorted = [...values].sort((a, b) => a - b);
  const index = (sorted.length - 1) * probability;
  const lowerIndex = Math.floor(index);
  const upperIndex = Math.ceil(index);
  const lower = sorted[lowerIndex];
  const upper = sorted[upperIndex];
  if (lower === undefined || upper === undefined) return Number.NaN;
  if (lowerIndex === upperIndex) return lower;
  return lower + (upper - lower) * (index - lowerIndex);
}

export function sampleVariance(values: readonly number[]): number {
  if (values.length < 2) return Number.NaN;
  const center = mean(values);
  return values.reduce((sum, value) => sum + (value - center) ** 2, 0) / (values.length - 1);
}

export function sampleStandardDeviation(values: readonly number[]): number {
  const variance = sampleVariance(values);
  return variance >= 0 ? Math.sqrt(variance) : Number.NaN;
}

export function medianAbsoluteDeviation(values: readonly number[]): number {
  const center = median(values);
  if (!Number.isFinite(center)) return Number.NaN;
  return median(values.map((value) => Math.abs(value - center)));
}

export function pearsonCorrelation(left: readonly number[], right: readonly number[]): number {
  if (left.length !== right.length || left.length < 2) return Number.NaN;
  const leftMean = mean(left);
  const rightMean = mean(right);
  let covariance = 0;
  let leftSquares = 0;
  let rightSquares = 0;
  for (let index = 0; index < left.length; index += 1) {
    const leftValue = left[index];
    const rightValue = right[index];
    if (leftValue === undefined || rightValue === undefined) return Number.NaN;
    const leftDelta = leftValue - leftMean;
    const rightDelta = rightValue - rightMean;
    covariance += leftDelta * rightDelta;
    leftSquares += leftDelta ** 2;
    rightSquares += rightDelta ** 2;
  }
  const denominator = Math.sqrt(leftSquares * rightSquares);
  return denominator > 0 ? covariance / denominator : Number.NaN;
}

export interface OlsFit {
  readonly coefficients: readonly number[];
  readonly residuals: readonly number[];
  readonly fitted: readonly number[];
  readonly covariance: readonly (readonly number[])[];
  readonly standardErrors: readonly number[];
  readonly rSquared: number;
  readonly sampleSize: number;
  readonly parameterCount: number;
}

function invertMatrix(matrix: readonly (readonly number[])[]): number[][] | null {
  const size = matrix.length;
  if (size === 0 || matrix.some((row) => row.length !== size)) return null;
  const augmented = matrix.map((row, rowIndex) => [
    ...row,
    ...Array.from({ length: size }, (_, columnIndex) => rowIndex === columnIndex ? 1 : 0),
  ]);

  for (let pivot = 0; pivot < size; pivot += 1) {
    let bestRow = pivot;
    let bestValue = Math.abs(augmented[pivot]?.[pivot] ?? 0);
    for (let row = pivot + 1; row < size; row += 1) {
      const candidate = Math.abs(augmented[row]?.[pivot] ?? 0);
      if (candidate > bestValue) {
        bestValue = candidate;
        bestRow = row;
      }
    }
    if (bestValue < 1e-12) return null;
    if (bestRow !== pivot) {
      const temporary = augmented[pivot];
      augmented[pivot] = augmented[bestRow] ?? [];
      augmented[bestRow] = temporary ?? [];
    }

    const pivotValue = augmented[pivot]?.[pivot];
    if (pivotValue === undefined) return null;
    for (let column = 0; column < size * 2; column += 1) {
      const value = augmented[pivot]?.[column];
      if (value === undefined) return null;
      if (augmented[pivot]) augmented[pivot]![column] = value / pivotValue;
    }
    for (let row = 0; row < size; row += 1) {
      if (row === pivot) continue;
      const factor = augmented[row]?.[pivot];
      if (factor === undefined) return null;
      for (let column = 0; column < size * 2; column += 1) {
        const rowValue = augmented[row]?.[column];
        const pivotRowValue = augmented[pivot]?.[column];
        if (rowValue === undefined || pivotRowValue === undefined) return null;
        if (augmented[row]) augmented[row]![column] = rowValue - factor * pivotRowValue;
      }
    }
  }

  return augmented.map((row) => row.slice(size));
}

function matrixMultiply(
  left: readonly (readonly number[])[],
  right: readonly (readonly number[])[],
): number[][] {
  const rows = left.length;
  const columns = right[0]?.length ?? 0;
  const shared = right.length;
  return Array.from({ length: rows }, (_, row) =>
    Array.from({ length: columns }, (_, column) => {
      let sum = 0;
      for (let index = 0; index < shared; index += 1) {
        sum += (left[row]?.[index] ?? 0) * (right[index]?.[column] ?? 0);
      }
      return sum;
    }),
  );
}

export function fitOls(
  dependent: readonly number[],
  design: readonly (readonly number[])[],
): OlsFit | null {
  const sampleSize = dependent.length;
  const parameterCount = design[0]?.length ?? 0;
  if (
    sampleSize !== design.length ||
    sampleSize <= parameterCount ||
    parameterCount === 0 ||
    dependent.some((value) => !Number.isFinite(value)) ||
    design.some((row) => row.length !== parameterCount || row.some((value) => !Number.isFinite(value)))
  ) return null;

  const xtx = Array.from({ length: parameterCount }, (_, row) =>
    Array.from({ length: parameterCount }, (_, column) =>
      design.reduce(
        (sum, observation) => sum + (observation[row] ?? 0) * (observation[column] ?? 0),
        0,
      ),
    ),
  );
  const inverse = invertMatrix(xtx);
  if (!inverse) return null;
  const xty = Array.from({ length: parameterCount }, (_, column) =>
    design.reduce(
      (sum, observation, index) => sum + (observation[column] ?? 0) * (dependent[index] ?? 0),
      0,
    ),
  );
  const coefficients = inverse.map((row) =>
    row.reduce((sum, value, index) => sum + value * (xty[index] ?? 0), 0),
  );
  const fitted = design.map((row) =>
    row.reduce((sum, value, index) => sum + value * (coefficients[index] ?? 0), 0),
  );
  const residuals = dependent.map((value, index) => value - (fitted[index] ?? 0));
  const rss = residuals.reduce((sum, value) => sum + value ** 2, 0);
  const sigmaSquared = rss / (sampleSize - parameterCount);
  const covariance = inverse.map((row) => row.map((value) => value * sigmaSquared));
  const standardErrors = covariance.map((row, index) => Math.sqrt(Math.max(0, row[index] ?? Number.NaN)));
  const center = mean(dependent);
  const tss = dependent.reduce((sum, value) => sum + (value - center) ** 2, 0);

  return {
    coefficients,
    residuals,
    fitted,
    covariance,
    standardErrors,
    rSquared: tss > 0 ? 1 - rss / tss : Number.NaN,
    sampleSize,
    parameterCount,
  };
}

export function clusterRobustStandardErrors(
  fit: OlsFit,
  design: readonly (readonly number[])[],
  clusters: readonly (string | number)[],
): readonly number[] | null {
  if (design.length !== fit.sampleSize || clusters.length !== fit.sampleSize) return null;
  const parameterCount = fit.parameterCount;
  const xtx = Array.from({ length: parameterCount }, (_, row) =>
    Array.from({ length: parameterCount }, (_, column) =>
      design.reduce((sum, observation) => sum + (observation[row] ?? 0) * (observation[column] ?? 0), 0),
    ),
  );
  const bread = invertMatrix(xtx);
  if (!bread) return null;
  const uniqueClusters = [...new Set(clusters)];
  if (uniqueClusters.length < 2) return null;
  const meat = Array.from({ length: parameterCount }, () => Array(parameterCount).fill(0) as number[]);
  for (const cluster of uniqueClusters) {
    const score = Array(parameterCount).fill(0) as number[];
    for (let index = 0; index < clusters.length; index += 1) {
      if (clusters[index] !== cluster) continue;
      for (let column = 0; column < parameterCount; column += 1) {
        score[column] = (score[column] ?? 0) + (design[index]?.[column] ?? 0) * (fit.residuals[index] ?? 0);
      }
    }
    for (let row = 0; row < parameterCount; row += 1) {
      for (let column = 0; column < parameterCount; column += 1) {
        if (meat[row]) meat[row]![column] = (meat[row]?.[column] ?? 0) + (score[row] ?? 0) * (score[column] ?? 0);
      }
    }
  }
  const covariance = matrixMultiply(matrixMultiply(bread, meat), bread);
  const groups = uniqueClusters.length;
  const correction = (groups / (groups - 1)) * ((fit.sampleSize - 1) / (fit.sampleSize - parameterCount));
  return covariance.map((row, index) => Math.sqrt(Math.max(0, (row[index] ?? Number.NaN) * correction)));
}

export interface SimpleOlsFit extends OlsFit {
  readonly alpha: number;
  readonly beta: number;
}

export function fitSimpleOls(x: readonly number[], y: readonly number[]): SimpleOlsFit | null {
  if (x.length !== y.length) return null;
  const fit = fitOls(y, x.map((value) => [1, value]));
  const alpha = fit?.coefficients[0];
  const beta = fit?.coefficients[1];
  if (!fit || alpha === undefined || beta === undefined) return null;
  return { ...fit, alpha, beta };
}
